import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';

import AutoSave from '@components/AutoSave';
import Divider from '@components/Divider';
import MultiValueAutocompleteField from '@components/Form/MultiValueAutocompleteField';
import { useEntitiesCreate, useProfileUpdate } from '@hooks';
import useEntitiesTop from '@hooks/useEntitiesTop';
import { autocompleteEntities } from '@utils/backend-api/entities';
import { AutocompleteRecord } from '@utils/interfaces';

import TopicsSchema from './TopicsSchema';

interface TopicsProps {
  isDisabled?: boolean;
  topics: AutocompleteRecord[];
  userId: number;
}

const Topics = ({ userId, topics, isDisabled }: TopicsProps) => {
  const initialState = useMemo(
    () => ({
      topics: topics || [],
    }),
    [topics]
  );
  const { mutateAsync: createEntityAsync } = useEntitiesCreate();
  const { saveProfile, showSave, saveStatus } = useProfileUpdate({ userId });
  const { data: topEntityOptions, isLoading: isLoadingTopEntityOptions } = useEntitiesTop();

  const handleSubmit = ({ topics }) => {
    const sanitizedParams = { topics: topics.map((i) => i.value) };
    saveProfile(sanitizedParams);
  };

  return (
    <Formik initialValues={initialState} onSubmit={handleSubmit} validationSchema={TopicsSchema}>
      {(formik) => (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <AutoSave debounceMs={500} />
          <div className="Topics section-container">
            <div className="section-header mt-2">Topics You Follow on The Information</div>
            <div className="section-detail">
              <div className="section-detail-container">
                {showSave && (
                  <div className={`save-label ${saveStatus === 'Saved' ? 'success' : 'failure'}`}>
                    {saveStatus}
                  </div>
                )}
              </div>
              <div className="section-form-container">
                <MultiValueAutocompleteField
                  placeholder="Type Topics"
                  label="My Topics"
                  name="topics"
                  isDisabled={isDisabled}
                  fetchMethod={(value) =>
                    autocompleteEntities({
                      fragment: value,
                      // relationship_sources that article entity_joins have
                      relationship_sources: ['chat_gpt', 'user_action'],
                      order_entities_by: ['is_important', 'pdl_verified'],
                    })
                  }
                  createMethod={(value) =>
                    createEntityAsync({
                      name: value,
                      entity_type: 'other',
                      word_type: 'common',
                    })
                  }
                  defaultOptions={isLoadingTopEntityOptions ? [] : topEntityOptions}
                />
              </div>
            </div>
            <Divider />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default Topics;
